import { makeAutoObservable } from 'mobx';
import api from '../config/api';
import { SystemSettings } from '../types';

export class SettingsStore {
    settings: SystemSettings | null = null;
    loading = false;
    error: string | null = null;
    saving = false;
    testResults: {
        database?: { status: 'success' | 'error' | 'testing' | null; message?: string };
        smtp?: { status: 'success' | 'error' | 'testing' | null; message?: string };
        mattermost?: { status: 'success' | 'error' | 'testing' | null; message?: string };
        crm?: { status: 'success' | 'error' | 'testing' | null; message?: string };
        tilda?: { status: 'success' | 'error' | 'testing' | null; message?: string };
    } = {};

    constructor() {
        makeAutoObservable(this);
    }

    async fetchSettings() {
        this.loading = true;
        this.error = null;

        try {
            const response = await api.get<SystemSettings>('/api/settings');
            this.settings = response.data;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to fetch settings';
        } finally {
            this.loading = false;
        }
    }

    async updateSettings(settingsData: SystemSettings): Promise<boolean> {
        this.saving = true;
        this.error = null;

        try {
            await api.put('/api/settings', settingsData);

            // Обновляем локальные настройки
            this.settings = { ...settingsData };
            return true;
        } catch (error: any) {
            this.error = error.response?.data?.error || 'Failed to update settings';
            return false;
        } finally {
            this.saving = false;
        }
    }

    async testConnection(service: 'database' | 'smtp' | 'mattermost' | 'crm') {
        this.testResults[service] = { status: 'testing' };

        try {
            if (service === 'smtp') {
                // Специальный endpoint для тестирования SMTP
                const response = await api.post('/api/test-email');
                this.testResults[service] = {
                    status: response.data.success ? 'success' : 'error',
                    message: response.data.message || response.data.error
                };
            } else {
                // Для других сервисов можно добавить соответствующие endpoints
                // Пока используем моковую реализацию
                await new Promise(resolve => setTimeout(resolve, 2000));
                const success = Math.random() > 0.3;
                this.testResults[service] = {
                    status: success ? 'success' : 'error',
                    message: success ? 'Соединение успешно установлено' : 'Не удалось установить соединение'
                };
            }
        } catch (error: any) {
            this.testResults[service] = {
                status: 'error',
                message: error.response?.data?.error || 'Не удалось протестировать соединение'
            };
        }
    }

    clearTestResult(service: 'database' | 'smtp' | 'mattermost' | 'crm') {
        delete this.testResults[service];
    }

    clearAllTestResults() {
        this.testResults = {};
    }

    get isDatabaseConfigured(): boolean {
        return !!(this.settings?.database.host && this.settings?.database.name);
    }

    get isSmtpConfigured(): boolean {
        return !!(this.settings?.smtp.host && this.settings?.smtp.username);
    }

    get isMattermostConfigured(): boolean {
        return !!(this.settings?.mattermost.url && this.settings?.mattermost.token);
    }

    get isCrmConfigured(): boolean {
        return !!(this.settings?.crm.bpm_url);
    }

    get configuredServicesCount(): number {
        let count = 0;
        if (this.isDatabaseConfigured) count++;
        if (this.isSmtpConfigured) count++;
        if (this.isMattermostConfigured) count++;
        if (this.isCrmConfigured) count++;
        return count;
    }

    get totalServicesCount(): number {
        return 4; // Database, SMTP, Mattermost, CRM
    }

    get configurationProgress(): number {
        return (this.configuredServicesCount / this.totalServicesCount) * 100;
    }

    resetError() {
        this.error = null;
    }

    clearSettings() {
        this.settings = null;
    }
}